import jsPDF from "jspdf";

export const PDF_FONT_FAMILY = "NotoSans";

const FONT_URLS = {
  normal: "/fonts/NotoSans-Regular.ttf",
  bold: "/fonts/NotoSans-Bold.ttf"
} as const;

type FontStyle = keyof typeof FONT_URLS;

const fetchAsBase64 = async (url: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to load PDF font ${url}: ${res.status}`);
  }
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize))
    );
  }
  return btoa(binary);
};

let fontPromise: Promise<Record<FontStyle, string>> | null = null;

const loadFontData = () => {
  if (!fontPromise) {
    fontPromise = (async () => {
      const [normal, bold] = await Promise.all([
        fetchAsBase64(FONT_URLS.normal),
        fetchAsBase64(FONT_URLS.bold)
      ]);
      return { normal, bold };
    })().catch((err) => {
      fontPromise = null;
      throw err;
    });
  }
  return fontPromise;
};

export const registerNotoSansFont = async (pdf: jsPDF): Promise<string> => {
  const data = await loadFontData();
  pdf.addFileToVFS("NotoSans-Regular.ttf", data.normal);
  pdf.addFont("NotoSans-Regular.ttf", PDF_FONT_FAMILY, "normal");
  pdf.addFileToVFS("NotoSans-Bold.ttf", data.bold);
  pdf.addFont("NotoSans-Bold.ttf", PDF_FONT_FAMILY, "bold");
  pdf.setFont(PDF_FONT_FAMILY, "normal");
  return PDF_FONT_FAMILY;
};

// jsPDF cannot render color emoji glyphs even from an embedded TTF.
// Strip Extended_Pictographic plus the variation selector (U+FE0F) and
// zero-width joiner (U+200D) used to compose emoji sequences, so they
// don't appear as tofu / missing-glyph boxes in the output.
const EMOJI_RE = /[\p{Extended_Pictographic}‍️]/gu;

export const sanitizePdfText = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.replace(EMOJI_RE, "").replace(/[ \t]+/g, " ").trim();
};
